/**
 * useCandidateStages Hook
 *
 * Loads the configurable Candidate Stage / Candidate Status hierarchy from the
 * backend (GET /api/v2/candidate-stages) via React Query. Master data, so it is
 * cached with a long stale time. Provides:
 *   - stages: grouped stages (ordered) each with ordered statuses + canEdit
 *   - statusById: fast id -> status (incl. stageName/stageCode/canEdit) lookup
 *   - flatStatuses: flat, ordered list for filter dropdowns
 *   - allowedNextFor(currentStatusId): stage-aware next statuses (AC2)
 */

import { useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { candidateStatusApi } from '../services/api'
import type { CandidateStage, CandidateStatusFlat } from '../types/candidateStatus'

interface UseCandidateStagesReturn {
  stages: CandidateStage[]
  statusById: Map<string, CandidateStatusFlat>
  flatStatuses: CandidateStatusFlat[]
  /** Statuses reachable from the given current status id (null = initial "Not Set"). */
  allowedNextFor: (currentStatusId: string | null) => CandidateStatusFlat[]
  isLoading: boolean
  isError: boolean
}

export function useCandidateStages(): UseCandidateStagesReturn {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate-stages'],
    queryFn: async () => {
      const res = await candidateStatusApi.getStages()
      return (res.data ?? []) as CandidateStage[]
    },
    // Master data, but refetch on mount so newly-seeded transitions/permissions
    // are always reflected (avoids stale allowedNextStatusIds after a seed).
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  })

  const stages = useMemo(() => {
    const list = (data ?? []).slice()
    list.sort((a, b) => a.displayOrder - b.displayOrder)
    return list.map((stage) => ({
      ...stage,
      statuses: (stage.statuses ?? []).slice().sort((a, b) => a.displayOrder - b.displayOrder),
    }))
  }, [data])

  const flatStatuses = useMemo<CandidateStatusFlat[]>(() => {
    return stages.flatMap((stage) =>
      stage.statuses.map((status) => ({
        ...status,
        stageName: stage.stageName,
        stageCode: stage.stageCode,
        canEdit: stage.canEdit ?? true,
      }))
    )
  }, [stages])

  const statusById = useMemo(() => {
    return new Map(flatStatuses.map((status) => [status.id, status]))
  }, [flatStatuses])

  const entryStatuses = useMemo(
    () => flatStatuses.filter((s) => s.isEntry),
    [flatStatuses]
  )

  const allowedNextFor = useCallback(
    (currentStatusId: string | null): CandidateStatusFlat[] => {
      if (!currentStatusId) return entryStatuses
      const current = statusById.get(currentStatusId)
      const ids = current?.allowedNextStatusIds ?? []
      return ids
        .map((id) => statusById.get(id))
        .filter((s): s is CandidateStatusFlat => Boolean(s))
    },
    [statusById, entryStatuses]
  )

  return { stages, statusById, flatStatuses, allowedNextFor, isLoading, isError }
}
